/**
 * Tipos compartilhados para o wizard de templates WABA
 * Arquitetura modular para criação e edição de templates
 */

// Tipo de step do wizard
export type WizardStep = 'config' | 'message';

// Definição de step com ícone e descrição
export interface StepDefinition {
  key: WizardStep;
  label: string;
  description: string;
}

// Interface para inbox oficial
export interface OfficialInbox {
  id: string;
  name: string;
  identifier: string;
  provider_id?: string;
  provider_slug?: string;
  provider_name?: string;
}

// Interface para classificação de template
export interface TemplateClassification {
  id: string;
  name: string;
  color: string;
}

// Interface para botão do template
// Tipos suportados pela Meta API conforme documentação oficial
export interface TemplateButton {
  type: 'URL' | 'PHONE_NUMBER' | 'QUICK_REPLY' | 'ORDER_DETAILS' | 'COPY_CODE';
  text: string;
  url?: string;
  url_type?: 'static' | 'dynamic';
  phone_number?: string;
  example_value?: string;
  // COPY_CODE - para templates de autenticação OTP
  code_example?: string;
  // ORDER_DETAILS - para pagamentos PIX Brasil
  pix_code?: string;
  merchant_name?: string;
  pix_key?: string;
  pix_key_type?: 'CNPJ' | 'CPF' | 'EMAIL' | 'CELULAR' | 'ALEATORIA';
  amount_value?: number;
  item_name?: string;
}

// Formatos de header disponíveis
export type HeaderFormat = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';

// Dados do formulário do wizard
export interface TemplateFormData {
  // Step 1: Configurações
  inboxId: string;
  name: string;
  category: string;
  language: string;
  classificationId: string | null;
  
  // Step 2: Mensagem
  headerFormat: HeaderFormat;
  headerText: string;
  headerMediaUrl: string;
  /** Nome do arquivo de mídia selecionado (para exibição no formulário) */
  headerMediaFilename: string;
  bodyText: string;
  footerText: string;
  buttons: TemplateButton[];
  exampleValues: Record<string, string>;
}

// Props da sidebar do wizard
export interface WizardSidebarProps {
  currentStep: WizardStep;
  onStepClick: (step: WizardStep) => void;
  isNextDisabled: boolean;
  summary: {
    inboxName?: string;
    category?: string;
    language?: string;
    buttonCount: number;
    hasHeader: boolean;
  };
}

// Props do painel de preview
export interface PreviewPanelProps {
  headerFormat: HeaderFormat;
  headerText: string;
  bodyText: string;
  footerText: string;
  buttons: TemplateButton[];
  exampleValues: Record<string, string>;
}

// Categorias disponíveis
export const CATEGORIES = [
  { value: 'MARKETING', label: 'Marketing', description: 'Promoções e ofertas' },
  { value: 'UTILITY', label: 'Utilitário', description: 'Notificações transacionais' },
  { value: 'AUTHENTICATION', label: 'Autenticação', description: 'Códigos de verificação' },
] as const;

// Idiomas suportados
export const LANGUAGES = [
  { value: 'pt_BR', label: 'Português (Brasil)' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
] as const;

// Formatos de header
export const HEADER_FORMATS = [
  { value: 'NONE', label: 'Sem Header' },
  { value: 'TEXT', label: 'Texto' },
  { value: 'IMAGE', label: 'Imagem' },
  { value: 'VIDEO', label: 'Vídeo' },
  { value: 'DOCUMENT', label: 'Documento' },
] as const;

// Tipos de botão - conforme documentação Meta API
export const BUTTON_TYPES = [
  { value: 'QUICK_REPLY', label: 'Resposta Rápida', description: 'Texto de resposta rápida' },
  { value: 'URL', label: 'Link URL', description: 'Abre uma URL externa' },
  { value: 'PHONE_NUMBER', label: 'Telefone', description: 'Inicia uma ligação' },
  { value: 'COPY_CODE', label: 'Copiar Código', description: 'Copia código OTP (autenticação)' },
  { value: 'ORDER_DETAILS', label: 'Pagamento PIX', description: 'Exibe detalhes do pagamento PIX' },
] as const;

// Limite máximo de botões conforme Meta API
export const MAX_BUTTONS = 10;
export const MAX_BUTTON_TEXT_LENGTH = 25;
